import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { fetchCollection } from '../lib/cms';
import { MessageCircle, Clock, FileText, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

export default function Presentations() {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollection('presentations').then(data => {
      // Sort presentations so "Relationskompetence" is first, "Hverdagens pædagogisk struktur" is second, and the rest follows
      const sortedData = [...data].sort((a, b) => {
        const titleA = a.title?.da || '';
        const titleB = b.title?.da || '';
        
        if (titleA === 'Relationskompetence') return -1;
        if (titleB === 'Relationskompetence') return 1;
        
        if (titleA === 'Hverdagens pædagogisk struktur') return -1;
        if (titleB === 'Hverdagens pædagogisk struktur') return 1;
        
        return 0;
      });
      setPresentations(sortedData);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-800"></div>
    </div>
  );

  return (
    <div className="pb-24">
      <SEO 
        title="Pædagogiske Oplæg & Workshops til Institutioner & Skoler"
        description="Book praksisnære pædagogiske oplæg og workshops til jeres personalegruppe, skole eller daginstitution. Styrk relationskompetencen og pædagogisk struktur."
        path="/oplaeg"
      />
      {/* Hero Section */}
      <section className="relative h-[55vh] md:h-[65vh] min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/de6688a6-c4fe-4d97-a4ed-fd0cbb034579.png" 
          alt="Abstrakt baggrund" 
          className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-70"
        />
        <div className="absolute inset-0 bg-emerald-900/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 md:pt-32">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-light text-white uppercase tracking-tight"
          >
            Pædagogiske oplæg
          </motion.h1>
        </div>

        {/* Wave Divider at bottom of Hero */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-white"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div className="border-l-4 border-[#4e6354] pl-6 py-2">
              <p className="text-xl text-stone-600 leading-relaxed font-light">
                Jeg tilbyder faglige oplæg og workshops til personale i både almindelige institutioner og specialinstitutioner. Mine oplæg er praksisnære, tager udgangspunkt i jeres specifikke hverdag og tilpasses altid jeres målgruppe og aktuelle udfordringer.
              </p>
            </div>


            
            <div className="space-y-6">
              <div className="border-b border-stone-100 pb-4 mb-6">
                <h3 className="text-2xl font-light text-stone-900 mt-4 uppercase tracking-tight">
                  Forslag til emner
                </h3>
              </div>

              <div className="grid gap-6">
                {presentations.map((item, index) => (
                  <div 
                    key={item.id} 
                    className="bg-white border border-stone-200/60 p-6 md:p-8 rounded-2xl shadow-sm hover:shadow-md hover:border-[#4e6354]/30 transition-all duration-300 group relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-stone-200 group-hover:bg-[#4e6354] transition-all duration-300" />
                    <div className="flex gap-4 items-start pl-2">
                      <span className="text-lg font-light text-[#4e6354]/60 group-hover:text-[#4e6354] transition-colors duration-300 font-mono">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-grow">
                        <h4 className="font-medium text-stone-900 mb-2 text-base uppercase tracking-wide group-hover:text-[#4e6354] transition-colors duration-300">
                          {item.title.da}
                        </h4>
                        <div 
                          className="text-stone-600 text-sm leading-relaxed prose prose-sm max-w-none mb-4"
                          dangerouslySetInnerHTML={{ __html: item.description.da }}
                        />
                        <Link 
                          to={`/kontakt?subject=${encodeURIComponent(item.title.da)}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4e6354] hover:text-[#3d4e42] transition-colors group/btn"
                        >
                          Vælg dette emne <span className="group-hover/btn:translate-x-1 transition-transform duration-200">→</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="bg-stone-50/60 border border-dashed border-stone-200 p-6 md:p-8 rounded-2xl group relative overflow-hidden">
                  <div className="flex gap-4 items-start pl-2">
                    <span className="text-lg font-light text-stone-400 font-mono">
                      +
                    </span>
                    <div>
                      <h4 className="font-medium text-stone-900 mb-2 text-base uppercase tracking-wide">
                        m.fl.
                      </h4>
                      <p className="text-stone-600 text-sm leading-relaxed">
                        Jeg tilrettelægger også gerne oplæg efter jeres specifikke ønsker og behov, så det passer præcis til jeres personalegruppe og hverdag.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Link 
                to="/kontakt" 
                className="inline-flex items-center gap-3 bg-[#4e6354] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#3d4e42] shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                Book et oplæg <MessageCircle className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8 lg:sticky lg:top-8"
          >
            <h2 className="text-2xl font-light text-stone-900 uppercase tracking-tight mb-6 border-b border-stone-200 pb-4">
              Inspiration til oplæg
            </h2>

            {/* Pakke 1 */}
            <div className="bg-gradient-to-br from-[#4e6354]/5 to-transparent border border-[#4e6354]/10 p-6 md:p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#4e6354] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  Pakke 1
                </span>
                <span className="text-stone-500 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4 text-[#4e6354]" /> 1-2 timer
                </span>
              </div>
              <h3 className="text-xl font-light text-stone-900 mb-6 uppercase">
                Det faglige indspark <span className="text-stone-500 block text-sm font-normal normal-case mt-1">(Personaleaften)</span>
              </h3>
              
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Varighed:</h4>
                    <p className="text-stone-600">1-2 timer (f.eks. kl. 17-19).</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Indhold:</h4>
                    <p className="text-stone-600">Et koncentreret oplæg med fokus på ét konkret emne, f.eks. pædagogisk organisering eller introduktion til Marte Meo-principperne.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <Target className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Formål:</h4>
                    <p className="text-stone-600">At give personalegruppen fælles inspiration, konkrete "her-og-nu"-redskaber og fornyet energi til hverdagen.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pakke 2 */}
            <div className="bg-gradient-to-br from-[#4e6354]/5 to-transparent border border-[#4e6354]/10 p-6 md:p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#4e6354] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  Pakke 2
                </span>
                <span className="text-stone-500 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4 text-[#4e6354]" /> Heldag
                </span>
              </div>
              <h3 className="text-xl font-light text-stone-900 mb-6 uppercase">
                Den pædagogiske dag <span className="text-stone-500 block text-sm font-normal normal-case mt-1">(Fordybelse)</span>
              </h3>
              
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Varighed:</h4>
                    <p className="text-stone-600">Heldag (f.eks. 5-6 timer).</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Indhold:</h4>
                    <p className="text-stone-600">En vekslen mellem faglige oplæg, workshops, refleksionsøvelser og gruppearbejde baseret på jeres institutions praksis.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <Target className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Formål:</h4>
                    <p className="text-stone-600">At gå i dybden med et emne, styrke jeres fælles pædagogiske fundament og lave en konkret handlingsplan for implementering i hverdagen.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pakke 3 */}
            <div className="bg-gradient-to-br from-[#4e6354]/5 to-transparent border border-[#4e6354]/10 p-6 md:p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#4e6354] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  Pakke 3
                </span>
                <span className="text-stone-500 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4 text-[#4e6354]" /> 2-3 mdr.
                </span>
              </div>
              <h3 className="text-xl font-light text-stone-900 mb-6 uppercase">
                Proces- og implementeringsforløbet <span className="text-stone-500 block text-sm font-normal normal-case mt-1">(Forandring der holder)</span>
              </h3>
              
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Varighed:</h4>
                    <p className="text-stone-600">Strækker sig over 2-3 måneder (f.eks. 1 x personalemøde + 2 x opfølgende aktionslæring/sparring).</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Indhold:</h4>
                    <p className="text-stone-600">Først et kortere teorioplæg for hele personalet. Derefter kommer jeg ud i praksis (f.eks. på stuerne) og observerer, hvorefter jeg giver pædagogisk sparring og opfølgning.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 p-1.5 bg-white rounded-lg text-[#4e6354] shadow-sm h-fit">
                    <Target className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800">Formål:</h4>
                    <p className="text-stone-600">At sikre, at den nye viden faktisk bliver til virkelighed i hverdagen og ikke bliver glemt. Perfekt til institutioner, der vil rykke sig på kulturen eller organiseringen til personalet på baggrund af det, vi aftalte.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
